import { SPI, Subscription, subscribeRoute } from '@kunlun/dependencies';
import { MaskWidget, MenuWidget, RuntimeMenu } from '@kunlun/vue-admin-layout';
import { createApp, defineComponent, h } from 'vue';
import antd from 'ant-design-vue';
import type { Component } from 'vue';
import MyPageVue from './MyPage.vue';
import ProductListVue from '../product-list/ProductList.vue';
import ReceivableCustomerVue from '../receivableOverdue/receivableCustomer/receivableCustomer.vue';
import ReceivableManageVue from '../receivableOverdue/receivableManage/receivableManage.vue';
import ReceivableHistoryVue from '../receivableOverdue/receivableHistory/receivableHistory.vue';
import ReceivableForecastVue from '../receivableOverdue/receivableForecast/receivableForecast.vue';
import ReceivableForecastSummaryVue from '../receivableOverdue/receivableForecastSummary/receivableForecastSummary.vue';

interface CustomPageOptions {
  title: string;
  breadcrumb?: string[];
}

type PageEntry = { component: Component; options: CustomPageOptions };

/**
 * menu.name → 自定义页面配置
 *
 * 新增自定义菜单页面时，只需：
 *   1. import 新的 .vue 文件
 *   2. 在此处追加一条记录 { 'menu.name 值': { component, options } }
 */
const MENU_NAME_MAP: Record<string, PageEntry> = {
  uiMenu2b2dfbf07d74494cb873e464212e0aa0: {
    component: MyPageVue,
    options: { title: '跳转商城', breadcrumb: ['首页', '跳转商城'] }
  },
  uiMenu2b2dfbf07d74494cb873e464212e0aa1: {
    component: ProductListVue,
    options: { title: '跳转自定义页面', breadcrumb: ['首页', '跳转测试', '跳转自定义页面'] }
  },
  // TODO: 替换为后端「应收客户」菜单的真实 menu.name
  uiMenua731d902f58641bc920e1f8c567d34b9: {
    component: ReceivableCustomerVue,
    options: {
      title: '应收客户',
      breadcrumb: ['首页', '应收管理', '应收客户']
    }
  },
  // TODO: 替换为后端「应收报表」菜单的真实 menu.name
  uiMenub8c4e2a17f394d6a9e5c0d1f2a3b4c5d: {
    component: ReceivableManageVue,
    options: {
      title: '应收报表',
      breadcrumb: ['首页', '应收管理', '应收报表']
    }
  },
  uiMenua1b2c3d4e5f64789a0b1c2d3e4f5a6b7: {
    component: ReceivableHistoryVue,
    options: {
      title: '应收报表历史归档',
      breadcrumb: ['首页', '应收管理', '应收报表历史归档']
    }
  },
  uiMenuc7d8e9f0a1b2c3d4e5f6a7b8c9d0e1f2: {
    component: ReceivableForecastVue,
    options: {
      title: '应收逾期预测',
      breadcrumb: ['首页', '应收管理', '应收逾期预测']
    }
  },
  uiMenud8e9f0a1b2c3d4e5f6a7b8c9d0e1f2a3: {
    component: ReceivableForecastSummaryVue,
    options: {
      title: '应收逾期预测汇总',
      breadcrumb: ['首页', '应收管理', '应收逾期预测汇总']
    }
  }
  // 'menu.name 值': {
  //   component: XxxPageVue,
  //   options: { title: '页面标题', breadcrumb: ['首页', '父菜单', '页面标题'] }
  // },
};

// ── 多实例全局状态 ─────────────────────────────────────────────────────────

interface OverlayInstance {
  /** 对应菜单项的 key（用于恢复左侧菜单高亮） */
  nodeKey: string;
  /** 对应菜单项展开的父菜单 keys（用于恢复左侧菜单展开状态） */
  savedOpenKeys: string[];
  show:            () => void;
  hide:            () => void;
  destroy:         () => void;
  applyBreadcrumb: () => void;
}

/** 最多同时保留的自定义 tab 数量 */
const MAX_CUSTOM_TABS = 10;

/** menuName → 实例 */
const instances = new Map<string, OverlayInstance>();

/** 当前可见的 menuName */
let activeMenuName: string | null = null;

/** Widget 实例引用（用于更新左侧菜单 selectedKeys / openKeys） */
let widgetRef: CustomMenuWidget | null = null;

// 框架状态快照（第一个实例创建时保存，最后一个销毁时恢复）
let savedFrameworkActiveTabs: HTMLElement[] = [];
let savedFrameworkSelectedKeys: string[] = [];
let savedFrameworkOpenKeys: string[] = [];
let originalMainBlockPosition = '';
let sharedMainBlockEl: HTMLElement | null = null;

// ── .k-layout-content overflow 管理 ──────────────────────────────────────────
// 问题：overlay position:absolute 只覆盖 .k-layout-block 的可视区域。
//       浏览器缩小时，框架内容（表格等）宽度溢出，用户横向滚动可见旧页面内容。
// 解法：overlay 可见时将 .k-layout-content 的 overflow 改为 hidden，阻止横向滚动。

let contentElRef: HTMLElement | null = null;

/**
 * overlay 可见时将 .k-layout-content 和 .k-layout-block 都设为 overflow:hidden：
 *   - .k-layout-content hidden → 消除横向滚动条，用户无法滚动看到框架内容
 *   - .k-layout-block   hidden → 阻止框架内容溢出进入 .k-layout-content 的 padding 区域
 * overlay 隐藏时，清除内联 style，回退到 CSS 类的默认值。
 */
function syncContentOverflow(): void {
  const hiding = activeMenuName !== null;
  if (contentElRef) contentElRef.style.overflow = hiding ? 'hidden' : '';
  if (sharedMainBlockEl) sharedMainBlockEl.style.overflow = hiding ? 'hidden' : '';
}

// ── 面包屑覆盖层（不改 Vue 托管节点，用独立 div 覆盖在上方）─────────────────
// 原因：直接写 .ant-breadcrumb.innerHTML 会破坏 Vue VNode 树，导致框架页面包屑也出错。
// 方案：创建一个 position:absolute 的兄弟 div，show/hide 时只切换 display，不碰 Vue 节点。

let breadcrumbOverlayEl: HTMLElement | null = null;
let nativeBreadcrumbEl: HTMLElement | null = null;

function ensureBreadcrumbOverlay(): HTMLElement | null {
  if (breadcrumbOverlayEl) return breadcrumbOverlayEl;

  const bcEl = document.querySelector<HTMLElement>('.k-layout-content .ant-breadcrumb');
  const parent = bcEl?.parentElement;
  if (!parent) return null;
  nativeBreadcrumbEl = bcEl;

  if (getComputedStyle(parent).position === 'static') {
    parent.style.position = 'relative';
  }

  breadcrumbOverlayEl = document.createElement('div');
  // 尽量完整复用原生面包屑 class，避免字体/间距/颜色不一致
  breadcrumbOverlayEl.className = bcEl.className || 'ant-breadcrumb';
  breadcrumbOverlayEl.style.cssText =
    'position:absolute;inset:0;z-index:10;display:none;';
  parent.appendChild(breadcrumbOverlayEl);
  return breadcrumbOverlayEl;
}

function showBreadcrumbOverlay(html: string): void {
  const el = ensureBreadcrumbOverlay();
  if (el) {
    el.innerHTML = html;
    el.style.display = 'block';
    if (nativeBreadcrumbEl) nativeBreadcrumbEl.style.visibility = 'hidden';
  }
}

function hideBreadcrumbOverlay(): void {
  if (breadcrumbOverlayEl) breadcrumbOverlayEl.style.display = 'none';
  if (nativeBreadcrumbEl) nativeBreadcrumbEl.style.visibility = '';
}

function destroyBreadcrumbOverlay(): void {
  breadcrumbOverlayEl?.remove();
  breadcrumbOverlayEl = null;
  if (nativeBreadcrumbEl) nativeBreadcrumbEl.style.visibility = '';
  nativeBreadcrumbEl = null;
}

// ── 框架 tab 点击拦截（解决 NavigationDuplicated 问题）──────────────────────
// 问题场景：overlay 打开时路由不变 → 用户点击已在当前路由的框架 tab → router.push 报 NavigationDuplicated
// 解法：capture 阶段监听，框架 tab 被点击时直接 deactivate overlay

let tabsClickInterceptor: ((e: MouseEvent) => void) | null = null;
// NavigationDuplicated 由 Kunlun 内部 RxJS subscriber 同步 throw，
// 需同时监听 'error'（同步）和 'unhandledrejection'（异步 Promise）两种形式
let navDupErrorSuppressor: ((e: ErrorEvent) => void) | null = null;
let navDupRejectionSuppressor: ((e: PromiseRejectionEvent) => void) | null = null;

function isNavigationDuplicated(err: unknown): boolean {
  return (err as { name?: string })?.name === 'NavigationDuplicated';
}

function setupTabsInterception(): void {
  const tabsEl = document.querySelector<HTMLElement>('#k-layout-multi-tabs');

  tabsClickInterceptor = (e: MouseEvent) => {
    if (activeMenuName === null) return;
    const clickedTab = (e.target as HTMLElement).closest<HTMLElement>('.k-layout-multi-tabs-tab');
    if (!clickedTab || clickedTab.getAttribute('data-custom-page') === 'true') return;

    // overlay 可见时路由不会改变（所有会改变路由的操作都先触发 overlay hide）。
    // 因此当前路由 === 打开 overlay 时保存的框架路由。
    // 若用户点击的正是 savedFrameworkActiveTabs 里的 tab（即当前路由对应的 tab），
    // Kunlun 的 router.push 必然报 NavigationDuplicated。
    // 解法：stopImmediatePropagation() 让 Kunlun 根本收不到这次点击，从源头消灭错误。
    const wouldBeDuplicate = savedFrameworkActiveTabs.includes(clickedTab);

    // 先隐藏 overlay
    deactivateCurrentInstance();

    if (wouldBeDuplicate) {
      // 阻止事件继续传播，Kunlun 不会尝试重复导航
      e.stopImmediatePropagation();
      // 手动恢复该 tab 的高亮（因为 Kunlun 不会再处理此点击）
      document.querySelectorAll<HTMLElement>('.k-layout-multi-tabs-tab-active').forEach((el) => {
        el.classList.remove('k-layout-multi-tabs-tab-active');
      });
      clickedTab.classList.add('k-layout-multi-tabs-tab-active');
    }
    // else：点击的是另一个框架 tab，让 Kunlun 正常处理（真实导航，不会重复）
  };
  tabsEl?.addEventListener('click', tabsClickInterceptor, true); // capture 阶段，先于 Kunlun 的 bubble 监听器

  // 兜底：极少数情况下仍出现的 NavigationDuplicated（异步 / 其他代码路径）
  navDupErrorSuppressor = (e: ErrorEvent) => {
    if (isNavigationDuplicated(e.error)) e.preventDefault();
  };
  window.addEventListener('error', navDupErrorSuppressor);

  navDupRejectionSuppressor = (e: PromiseRejectionEvent) => {
    if (isNavigationDuplicated(e.reason)) e.preventDefault();
  };
  window.addEventListener('unhandledrejection', navDupRejectionSuppressor);
}

function teardownTabsInterception(): void {
  const tabsEl = document.querySelector<HTMLElement>('#k-layout-multi-tabs');
  if (tabsClickInterceptor) {
    tabsEl?.removeEventListener('click', tabsClickInterceptor, true);
    tabsClickInterceptor = null;
  }
  if (navDupErrorSuppressor) {
    window.removeEventListener('error', navDupErrorSuppressor);
    navDupErrorSuppressor = null;
  }
  if (navDupRejectionSuppressor) {
    window.removeEventListener('unhandledrejection', navDupRejectionSuppressor);
    navDupRejectionSuppressor = null;
  }
}

// ── 多实例辅助函数 ─────────────────────────────────────────────────────────

/** 激活指定实例（隐藏当前激活实例，显示目标实例，同步左侧菜单） */
function activateInstance(menuName: string): void {
  if (activeMenuName === menuName) return;

  if (activeMenuName) {
    instances.get(activeMenuName)?.hide();
  }

  const target = instances.get(menuName);
  if (target) {
    target.show();
    target.applyBreadcrumb();
    activeMenuName = menuName;
    syncContentOverflow(); // overlay 可见 → overflow:hidden

    // 同步左侧菜单高亮 & 展开状态（通过 class 静态方法访问 protected 属性）
    CustomMenuWidget.syncMenuState([target.nodeKey], target.savedOpenKeys);
  }
}

/** 隐藏当前激活实例（保留 tab，不销毁） */
function deactivateCurrentInstance(): void {
  if (activeMenuName) {
    instances.get(activeMenuName)?.hide();
    activeMenuName = null;
    syncContentOverflow(); // 无 overlay 可见 → 还原 overflow
  }
}

/** 实例自身完成销毁后，处理后续激活与全局状态清理 */
function handleInstanceDestroyed(menuName: string): void {
  const wasActive = activeMenuName === menuName;
  instances.delete(menuName);

  if (wasActive) {
    activeMenuName = null;

    if (instances.size > 0) {
      // 还有其他实例：激活最后一个
      const keys = [...instances.keys()];
      activateInstance(keys[keys.length - 1]);
    } else {
      // 最后一个实例：还原完整框架状态
      restoreFrameworkState();
    }
  }

  if (instances.size === 0) {
    teardownTabsInterception();
    savedFrameworkActiveTabs   = [];
    savedFrameworkSelectedKeys = [];
    savedFrameworkOpenKeys     = [];
    sharedMainBlockEl          = null;
    contentElRef               = null;
  }
}

/** 对外统一关闭入口（通过实例自身的 destroy 流程卸载 app + 取消订阅） */
function closeInstance(menuName: string): void {
  instances.get(menuName)?.destroy();
}

/** 超出上限时，关闭最早打开的实例 */
function evictOldestIfNeeded(): void {
  if (instances.size < MAX_CUSTOM_TABS) return;
  const oldest = instances.keys().next().value;
  if (typeof oldest === 'string') closeInstance(oldest);
}

/** 还原框架的 tab active 状态、面包屑覆盖层、左侧菜单、mainBlockEl position、overflow */
function restoreFrameworkState(): void {
  // 恢复框架 tab 高亮
  savedFrameworkActiveTabs.forEach((el) => {
    if (el.isConnected) el.classList.add('k-layout-multi-tabs-tab-active');
  });

  // 销毁面包屑覆盖层 → Vue 的 .ant-breadcrumb 自然显示（不干扰 Vue VNode 树）
  destroyBreadcrumbOverlay();

  // 恢复左侧菜单高亮 & 展开状态
  CustomMenuWidget.syncMenuState(savedFrameworkSelectedKeys, savedFrameworkOpenKeys);

  // 恢复 mainBlockEl position
  if (sharedMainBlockEl) {
    sharedMainBlockEl.style.position = originalMainBlockPosition;
  }

  // 恢复 .k-layout-content 的 overflow（activeMenuName 已为 null）
  syncContentOverflow();
}

// ── Widget 类 ─────────────────────────────────────────────────────────────

@SPI.ClassFactory(MaskWidget.Token({ widget: 'nav-menu' }))
export class CustomMenuWidget extends MenuWidget {

  /**
   * 同步左侧菜单的选中 & 展开状态。
   * 设为 static 的原因：模块级函数无法直接访问 protected 属性，
   * 通过 static 方法在类作用域内操作 widgetRef 可绕过此限制。
   */
  static syncMenuState(selectedKeys: string[], openKeys: string[]): void {
    if (!widgetRef) return;
    widgetRef.selectedKeys = selectedKeys;
    widgetRef.openKeys     = openKeys;
  }

  /**
   * 拦截所有菜单点击，分三种情况：
   *
   *  A. 点击「已打开的同一自定义菜单」→ 激活（show），同步左侧菜单
   *  B. 点击「尚未打开的自定义菜单」   → 创建新 tab
   *  C. 点击「普通框架菜单」           → 仅隐藏当前自定义页（保留 tab），正常导航
   */
  protected async onClick(node: any): Promise<void> {
    // 保持 widget 引用，供 activateInstance / restoreFrameworkState 使用
    widgetRef = this;

    const menu: RuntimeMenu = node?.value;
    const menuName = menu?.name ?? '';
    const entry = MENU_NAME_MAP[menuName];

    if (entry) {
      // 先更新左侧菜单选中状态（无论是激活还是新建都需要）
      this.selectedKeys = [node.key];

      if (instances.has(menuName)) {
        // A：已打开 → 激活（openKeys 由 activateInstance 内恢复）
        activateInstance(menuName);
      } else {
        // B：新页面 → 隐藏当前激活实例，创建新实例
        deactivateCurrentInstance();
        CustomMenuWidget.createInstance(
          menuName,
          node.key,
          [...(this.openKeys ?? [])],
          entry.component,
          entry.options
        );
      }
      return;
    }

    // C：普通菜单 → 仅隐藏自定义页（保留 tab），交给框架
    deactivateCurrentInstance();
    return super.onClick(node);
  }

  /**
   * 创建并挂载一个自定义页面实例（overlay + tab）
   */
  static createInstance(
    menuName: string,
    nodeKey: string,
    savedOpenKeys: string[],
    pageComponent: Component,
    options: CustomPageOptions
  ): void {
    const contentEl = document.querySelector<HTMLElement>('.k-layout-content');
    if (!contentEl) {
      console.error('[CustomMenuWidget] 未找到 .k-layout-content');
      return;
    }

    // 容量控制：超限自动关闭最早的 tab
    evictOldestIfNeeded();

    const mainBlockEl = contentEl.querySelector<HTMLElement>(':scope > .k-layout-block') ?? contentEl;

    // ── 第一个实例：保存完整框架状态快照 & 设置 tab 拦截 ─────────────
    if (instances.size === 0) {
      setupTabsInterception();

      contentElRef                 = contentEl;
      sharedMainBlockEl            = mainBlockEl;
      originalMainBlockPosition    = mainBlockEl.style.position;
      mainBlockEl.style.position   = 'relative';

      // 框架 tab active 状态
      const tabsEl = document.querySelector<HTMLElement>('#k-layout-multi-tabs');
      savedFrameworkActiveTabs = [];
      tabsEl?.querySelectorAll<HTMLElement>('.k-layout-multi-tabs-tab-active').forEach((el) => {
        el.classList.remove('k-layout-multi-tabs-tab-active');
        savedFrameworkActiveTabs.push(el);
      });

      // 左侧菜单状态
      savedFrameworkSelectedKeys = [...(widgetRef?.selectedKeys ?? [])];
      savedFrameworkOpenKeys     = [...(widgetRef?.openKeys    ?? [])];
    } else {
      // 后续实例：隐藏当前激活实例
      if (activeMenuName) {
        instances.get(activeMenuName)?.hide();
      }
    }

    // overlay 即将显示：阻止框架内容横向溢出（所有实例都需要，不只是第一个）
    // .k-layout-content hidden → 无横向滚动条
    // .k-layout-block   hidden → 内容不溢入 .k-layout-content 的 padding 区域
    contentElRef!.style.overflow = 'hidden';
    mainBlockEl.style.overflow   = 'hidden';

    // ── 面包屑（不改 Vue 托管节点，用覆盖层遮盖）────────────────────
    const breadcrumbItems = options.breadcrumb?.length ? options.breadcrumb : [options.title];
    const customBreadcrumbHTML = breadcrumbItems
      .map((item, i) => {
        const isLast = i === breadcrumbItems.length - 1;
        const link = `<span class="ant-breadcrumb-link">${item}</span>`;
        const sep = isLast ? '' : '<span class="ant-breadcrumb-separator">/</span>';
        return `<span>${link}${sep}</span>`;
      })
      .join('');

    function applyBreadcrumb() {
      showBreadcrumbOverlay(customBreadcrumbHTML);
    }

    applyBreadcrumb();

    // ── 覆盖层 ────────────────────────────────────────────────────
    const overlayEl = document.createElement('div');
    overlayEl.style.cssText =
      'position:absolute;top:0;left:0;width:100%;height:100%;z-index:100;background:#f0f2f5;overflow:auto;';
    mainBlockEl.appendChild(overlayEl);

    // ── 渲染 Vue 组件 ─────────────────────────────────────────────
    const Wrapper = defineComponent({
      setup: () => () => h(pageComponent as any, { onClose: () => closeInstance(menuName) })
    });
    const app = createApp(Wrapper);
    app.use(antd);
    app.mount(overlayEl);

    // ── 注入 Tab ──────────────────────────────────────────────────
    const tabEl = CustomMenuWidget.injectTab(options.title, {
      onClose: () => closeInstance(menuName),
      onClick: () => activateInstance(menuName)
    });

    // ── 实例状态机 ────────────────────────────────────────────────
    let isVisible = true;
    let destroyed = false;

    function hide() {
      if (!isVisible || destroyed) return;
      isVisible = false;
      overlayEl.style.display = 'none';
      tabEl.classList.remove('k-layout-multi-tabs-tab-active');
      hideBreadcrumbOverlay();   // 隐藏面包屑覆盖层 → Vue 的真实面包屑自然恢复
    }

    function show() {
      if (isVisible || destroyed) return;
      isVisible = true;
      overlayEl.style.display = '';

      // 清除其他所有 tab（框架 tab + 其他自定义 tab）的 active 状态
      const tabsEl = document.querySelector('#k-layout-multi-tabs');
      tabsEl?.querySelectorAll<HTMLElement>('.k-layout-multi-tabs-tab-active').forEach((el) => {
        if (el !== tabEl) el.classList.remove('k-layout-multi-tabs-tab-active');
      });
      tabEl.classList.add('k-layout-multi-tabs-tab-active');
    }

    function destroy() {
      if (destroyed) return;
      destroyed = true;

      routeSubscription?.unsubscribe();
      routeSubscription = null;

      tabEl.remove();
      app.unmount();
      overlayEl.remove();

      handleInstanceDestroyed(menuName);
    }

    // 路由变化：仅隐藏当前激活的实例（其他后台实例不受影响）
    let skipFirst = true;
    let routeSubscription: Subscription | null = subscribeRoute(
      () => {
        if (skipFirst) { skipFirst = false; return; }
        if (activeMenuName === menuName) {
          hide();
          activeMenuName = null;
          syncContentOverflow(); // 无 overlay 可见 → 还原 overflow
        }
      },
      { distinct: true }
    );

    // 注册实例
    instances.set(menuName, { nodeKey, savedOpenKeys, show, hide, destroy, applyBreadcrumb });
    activeMenuName = menuName;
  }

  /**
   * 在 #k-layout-multi-tabs 末尾插入一个 tab 元素并返回它。
   */
  private static injectTab(
    title: string,
    handlers: { onClose: () => void; onClick: () => void }
  ): HTMLElement {
    const tabsEl = document.querySelector<HTMLElement>('#k-layout-multi-tabs');

    const tabEl = document.createElement('div');
    tabEl.className =
      'k-layout-multi-tabs-tab k-layout-multi-tabs-tab-closable k-layout-multi-tabs-tab-active';
    tabEl.setAttribute('data-custom-page', 'true');

    const titleEl = document.createElement('div');
    titleEl.className = 'k-layout-multi-tabs-tab-title';
    titleEl.title = title;
    titleEl.textContent = title;

    const closeBtnWrap = document.createElement('div');
    closeBtnWrap.className = 'k-layout-multi-tabs-tab-close-btn';

    const closeBtn = document.createElement('button');
    closeBtn.className = 'ant-btn ant-btn-link oio-button oio-button-link';
    closeBtn.type = 'button';
    closeBtn.innerHTML =
      '<span class="oio-icon oio-iconfont-font-class" style="--oio-icon-font-size:16px;font-style:normal;">✕</span>';
    closeBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      handlers.onClose();
    });

    closeBtnWrap.appendChild(closeBtn);
    tabEl.appendChild(titleEl);
    tabEl.appendChild(closeBtnWrap);

    tabEl.addEventListener('click', (e) => {
      // 阻止冒泡，防止 Kunlun 的事件委托处理此点击触发路由跳转，
      // 导致 subscribeRoute 把刚 show() 的覆盖层又 hide() 掉
      e.stopPropagation();
      if ((e.target as HTMLElement).closest('.k-layout-multi-tabs-tab-close-btn')) return;
      handlers.onClick();
    });

    if (tabsEl) {
      tabsEl.appendChild(tabEl);
    } else {
      console.warn('[CustomMenuWidget] 未找到 #k-layout-multi-tabs，tab 注入跳过');
    }

    return tabEl;
  }
}
